import React from "react";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import campaignPauseIcon from "../../assets/images/svg/campaign-pause.svg";
import campaignDuplicateIcon from "../../assets/images/svg/campaing-duplicate.svg";
import campaignStartAgainIcon from "../../assets/images/svg/campaign-start-again.svg";
import campaignEditIcon from "../../assets/images/svg/meta-campaign-edit.svg";
import restoreIcon from "../../assets/images/svg/meta-campaign-edit-black.svg";
import campaignDeleteIcon from "../../assets/images/svg/campaignDeleteIcon.svg";
import { toast } from "react-toastify";
interface Campaign {
  id: string | number;
  name: string;
  status: string;
  conversionPage: string;
  startDate: string;
  endDate: string;
  budget: string;
  lastUpdated: string;
  isDraft?: boolean;
  linkUrls?: string[];
  videos?: { id: string; title: string }[];
  performance?: {
    dateRange: string;
    reach: number;
    impressions: number;
    clicks: number;
    spend: number;
    conversions: number;
  };
}

interface CampaignCardProps {
  campaign: Campaign;
  index: number;
  onDuplicate: (id: string | number) => void;
  onPause: (id: string | number) => void;
  onDelete: (id: string | number, isDraft: boolean) => void;
  onEdit?: (id: string | number) => void;
  isUpdating?: boolean;
  isDuplicating?: boolean;
  isDeleting?: boolean;
}

export default function MetaBuilderCampaignCard({
  campaign,
  index,
  onDuplicate,
  onPause,
  onDelete,
  onEdit,
  isUpdating,
  isDuplicating,
  isDeleting,
}: CampaignCardProps) {
  const navigate = useNavigate();

  // Parse budget string to get daily budget value
  // Format can be: "€500 total (€5/day)" or "€100 (€100/day)"
  const getDailyBudgetFromBudgetString = (
    budgetStr: string,
  ): { amount: number; currency: string } | null => {
    try {
      // Match pattern like "€5/day" or "$5/day"
      const dailyMatch = budgetStr.match(/[\¥£$€₹₽₩¢₴₸₪]\d+(?:\.\d+)?\/day/);
      if (dailyMatch) {
        const currency = dailyMatch[0].split("/")[0];
        const amount = parseFloat(currency.replace(/[^\d.]/g, ""));
        const currencySymbol = currency.match(/[^\d.]/)?.[0] || "$";
        return { amount, currency: currencySymbol };
      }
    } catch (e) {
      console.error("Error parsing budget string:", e);
    }
    return null;
  };

  // Calculate days between two dates
  const getDaysBetweenDates = (
    startStr: string,
    endStr: string,
  ): number | null => {
    try {
      const start = new Date(startStr);
      const end = new Date(endStr);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (e) {
      console.error("Error calculating days:", e);
      return null;
    }
  };

  // Get budget display based on end date
  const getBudgetDisplay = (): string => {
    const dailyBudget = getDailyBudgetFromBudgetString(campaign.budget);

    // Check if campaign has an end date (not "None" and not empty)
    const hasEndDate =
      campaign.endDate &&
      campaign.endDate !== "None" &&
      campaign.endDate.trim() !== "";

    if (dailyBudget) {
      if (hasEndDate) {
        // Calculate total budget: daily × days
        const days = getDaysBetweenDates(campaign.startDate, campaign.endDate);
        if (days !== null && days > 0) {
          const total = dailyBudget.amount * days;
          return `${dailyBudget.currency}${total.toFixed(2)} (${dailyBudget.currency}${dailyBudget.amount.toFixed(2)}/day)`;
        }
      }
      // No end date - show daily budget only
      return `${dailyBudget.currency}${dailyBudget.amount.toFixed(2)}/day`;
    }

    // Fallback to original budget string if parsing fails
    return campaign.budget;
  };

  function DetailRow({
    label,
    value,
  }: {
    label: string;
    value: React.ReactNode;
  }) {
    return (
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-6 w-full">
        <span className="text-white/90 md:text-white min-w-0 md:min-w-[160px] text-[14px] md:text-[16px] font-[400]">
          {label}
        </span>
        <div className="text-white text-left md:text-right min-w-0 md:max-w-[65%] break-words">
          {value}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 md:gap-6  relative self-stretch w-full bg-[#1A1B1F] rounded-[20px] md:rounded-[20px] p-[24px] md:p-6 ">
      {/* STEP 1 HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3 order-2 md:order-1 min-w-0">
          <div className="w-[36px] h-[36px] rounded-[11250px] bg-white opacity-[1] flex items-center justify-center">
            <span className="text-black text-[22.5px] font-[500] leading-[100%] tracking-[0]">
              {index + 1}
            </span>
          </div>
          <h3 className="text-white text-[18px] md:text-[18px] font-medium leading-[120%] max-w-full md:max-w-[220px] break-words">
            {campaign.name}
          </h3>
        </div>

        <div className="flex items-center gap-2 md:gap-3 shrink-0 order-1 md:order-2 md:ml-auto w-full md:w-auto justify-between md:justify-end">
          <span
            className={`px-3 md:px-4 py-2 rounded-[999px] text-black text-[12px] md:text-[18px] font-[600] leading-[100%] tracking-[0] ${
              campaign.status === "Active"
                ? "bg-[#1ED760]"
                : campaign.status === "Draft"
                  ? "bg-[#F2F3ED]"
                  : "bg-[#FFD23D]"
            }`}
          >
            {campaign.isDraft ? "draft" : campaign.status}
          </span>
          {!campaign.isDraft && (
            <div
              className="flex gap-2 md:gap-4 cursor-pointer hover:opacity-80 transition-opacity items-center"
              onClick={() =>
                onEdit
                  ? onEdit(campaign.id)
                  : navigate(`/create-meta-ads?campaignId=${campaign.id}`)
              }
            >
              <h4 className="text-[#FFFFFF] text-[14px] md:text-[16px] font-semibold">
                Edit
              </h4>
              <img
                src={campaignEditIcon}
                alt="Edit"
                className="w-5 h-5 md:w-6 md:h-6"
              />
            </div>
          )}
        </div>
      </div>

      <div className="w-full h-px bg-white/5 mt-1 md:mt-[20px]" />
      {/* DETAILS */}
      <div className="flex flex-col gap-0 text-sm">
        <div className="py-3 md:p-4 border-b border-white/5 mt-0 md:mt-[-20px]">
          <DetailRow
            label="Conversion page"
            value={
              <div className="flex flex-col gap-2 items-start md:items-end">
                {campaign.linkUrls && campaign.linkUrls.length > 0 ? (
                  campaign.linkUrls.map((url, index) => (
                    <span
                      key={index}
                      className="text-[#58F2A9] underline cursor-pointer text-[14px] md:text-[16px] font-medium"
                      onClick={() => window.open(url, "_blank")}
                    >
                      Smart Link
                    </span>
                  ))
                ) : (
                  <span className="text-white/40 text-[14px]">
                    No links available
                  </span>
                )}
              </div>
            }
          />
        </div>

        {/* <div className="p-4 border-b border-white/5">
          <DetailRow
            label="Conversion page"
            value={
              <span className="text-[#58F2A9] underline cursor-pointer">
                {campaign.conversionPage}
              </span>
            }
          />
        </div> */}

        <div className="py-3 md:p-4 border-b border-white/5">
          <DetailRow
            label="Schedule"
            value={
              <div className="flex gap-2 flex-wrap justify-start md:justify-end">
                <span className="px-4 py-2 rounded-full bg-[#2A2E38] text-[14px]">
                  Start date: {campaign.startDate}
                </span>
                <span className="px-4 py-2 rounded-full bg-[#2A2E38] text-[14px]">
                  End date: {campaign.endDate}
                </span>
              </div>
            }
          />
        </div>

        <div className="py-3 md:p-4 border-b border-white/5">
          <span className="text-[16px]">
            <DetailRow label="Budget" value={getBudgetDisplay()} />
          </span>
        </div>

        <div className="py-3 md:p-4 border-b border-white/5">
          <DetailRow
            label={
              campaign.videos && campaign.videos.length > 1
                ? "Ad Creatives"
                : "Ad Creative"
            }
            value={
              <div className="flex flex-col gap-2 items-start md:items-end">
                {campaign.videos && campaign.videos.length > 0 ? (
                  campaign.videos.map((video, index) => (
                    <span key={index} className="text-white text-[14px]">
                      {video.title}
                    </span>
                  ))
                ) : (
                  <span className="text-white/40 text-[14px]">
                    No videos available
                  </span>
                )}
              </div>
            }
          />
        </div>

        <div className="py-3 md:p-4 border-b border-white/5">
          <DetailRow
            label="Reach"
            value={
              <span className="px-4 py-2 rounded-full bg-[#2A2E38] text-[14px]">
                {campaign.performance?.reach ?? 0}
              </span>
            }
          />
        </div>

        <div className="py-3 md:p-4 border-b border-white/5">
          <DetailRow
            label="Impressions"
            value={
              <span className="px-4 py-2 rounded-full bg-[#2A2E38] text-[14px]">
                {campaign.performance?.impressions ?? 0}
              </span>
            }
          />
        </div>

        <div className="py-3 md:p-4 border-b border-white/5">
          <DetailRow
            label="Clicks"
            value={
              <span className="px-4 py-2 rounded-full bg-[#2A2E38] text-[14px]">
                {campaign.performance?.clicks ?? 0}
              </span>
            }
          />
        </div>

        <div className="py-3 md:p-4 border-b border-white/5">
          <DetailRow
            label="Conversions"
            value={
              <span className="px-4 py-2 rounded-full bg-[#2A2E38] text-[14px]">
                {campaign.performance?.conversions ?? 0}
              </span>
            }
          />
        </div>

        <div className="py-3 md:p-4 border-b border-white/5">
          <DetailRow
            label="Amount spent"
            value={
              <span className="px-4 py-2 rounded-full bg-[#2A2E38] text-[14px]">
                ${(campaign.performance?.spend ?? 0).toFixed(2)}
              </span>
            }
          />
        </div>

        <div className="py-3 md:p-4 text-left md:text-right text-white/40 text-[12px] md:text-[16px]">
          Last updated: {campaign.lastUpdated}
        </div>
      </div>
      <div className="flex flex-col md:flex-row justify-end items-stretch md:items-center gap-3 md:gap-6 pt-2 mt-0 md:mt-[-20px]">
        {campaign.isDraft ? (
          <>
            {/* Keep Editing */}
            <button
              onClick={() =>
                navigate(`/create-meta-ads?draftId=${campaign.id}`)
              }
              className="inline-flex items-center justify-center gap-2 rounded-[999px] bg-[#FFFFFF] text-[#03090D] text-[16px] md:text-[18px] font-[600] w-full md:w-[200px] h-[48px]"
            >
              <img src={restoreIcon} alt="Edit" className="w-6 h-6" />
              <span>Keep editing</span>
            </button>

            {/* Delete (Draft) */}
            <button
              onClick={() => !isDeleting && onDelete(campaign.id, true)}
              disabled={isDeleting}
              className="inline-flex items-center justify-center md:justify-start gap-2 text-white text-[16px] md:text-[18px] font-[500] hover:opacity-80 disabled:opacity-50"
            >
              {isDeleting ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                <>
                  <span>Delete</span>
                  <img
                    src={campaignDeleteIcon}
                    alt="Delete"
                    className="w-6 h-6"
                  />
                </>
              )}
            </button>
          </>
        ) : campaign.status === "Active" ? (
          <>
            {/* Duplicate */}
            <button
              onClick={() => !isDuplicating && onDuplicate(campaign.id)}
              disabled={isDuplicating || isUpdating}
              className="inline-flex items-center justify-center gap-2 rounded-[999px] bg-white text-black text-[16px] md:text-[18px] font-[600] w-full md:w-[185px] h-[48px]"
            >
              <img src={campaignDuplicateIcon} className="w-6 h-6" />
              <span>Duplicate</span>
            </button>

            {/* Pause */}
            <button
              onClick={() => !isUpdating && onPause(campaign.id)}
              disabled={isUpdating || isDuplicating}
              className="inline-flex items-center justify-center gap-2 rounded-[999px] bg-[#FFA3A3] text-[#03090D] text-[16px] md:text-[18px] font-[600] w-full md:w-[152px] h-[48px]"
            >
              <img src={campaignPauseIcon} className="w-6 h-6" />
              <span>Pause</span>
            </button>

            {/* Delete */}
            <button
              onClick={() => !isDeleting && onDelete(campaign.id, false)}
              disabled={isDeleting || isDuplicating || isUpdating}
              className="inline-flex items-center justify-center md:justify-start gap-2 text-white text-[16px] md:text-[18px] font-[500] hover:opacity-80 disabled:opacity-50"
            >
              {isDeleting ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                <>
                  <span>Delete</span>
                  <img
                    src={campaignDeleteIcon}
                    alt="Delete"
                    className="w-6 h-6"
                  />
                </>
              )}
            </button>
          </>
        ) : (
          <>
            {/* Start Again */}
            <button
              onClick={() => !isUpdating && onPause(campaign.id)}
              disabled={isUpdating}
              className="inline-flex items-center justify-center gap-2 rounded-[999px] bg-[#62EDFF] text-black text-[16px] md:text-[18px] font-[600] w-full md:w-[185px] h-[48px]"
            >
              <img src={campaignStartAgainIcon} className="w-6 h-6" />
              <span>Start again</span>
            </button>

            {/* Delete */}
            <button
              onClick={() => !isDeleting && onDelete(campaign.id, false)}
              disabled={isDeleting || isUpdating}
              className="inline-flex items-center justify-center md:justify-start gap-2 text-white text-[16px] md:text-[18px] font-[500] hover:opacity-80 disabled:opacity-50"
            >
              {isDeleting ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                <>
                  <span>Delete</span>
                  <img
                    src={campaignDeleteIcon}
                    alt="Delete"
                    className="w-6 h-6"
                  />
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
